import { useState } from 'react';

import Iconify from '@/components/iconify/iconify';
import { Button } from '@/components/ui/button';
import { Collapse } from '@mui/material';
import { EnhancedTableToolbarProps } from './type';

export const EnhancedTableToolbar = ({
  FilterComponent,
  filterColumn,
  searchFunction,
  searchValue,
  propSearch,
  setPropSearch,
  btnAction,
  btnText,
  onClearFitler,
}: EnhancedTableToolbarProps) => {
  // Start collapsed on mobile to save space, expanded on desktop
  const [collapse, setCollapse] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return true;
  });

  const handleClickFilter = () => {
    setCollapse(!collapse);
  };

  return (
    <div className="border-b border-gray-200 p-2 md:p-3">
      {/* Filter Header */}
      <div className="flex items-center justify-between md:justify-end mb-1 md:mb-2">
        {/* Mobile: Show filter toggle on left, desktop: on right */}
        <div className="flex items-center space-x-1.5 md:space-x-2">
          {/* Toggle Filter Button */}
          <Button
            size="sm"
            variant="outline"
            onClick={handleClickFilter}
            className="text-xs md:text-sm h-7 md:h-8 px-2 md:px-3"
          >
            {collapse ? (
              <Iconify icon="lucide:chevron-down" className="w-3.5 h-3.5 md:w-4 md:h-4 md:mr-1" />
            ) : (
              <Iconify icon="lucide:chevron-up" className="w-3.5 h-3.5 md:w-4 md:h-4 md:mr-1" />
            )}
            <span className="hidden md:inline">{collapse ? 'Hide Filters' : 'Show Filters'}</span>
            <span className="md:hidden text-[10px]">{collapse ? 'Filters' : 'Hide'}</span>
          </Button>

          {/* Clear Filter Button */}
          <Button
            size="sm"
            variant="ghost"
            color="error"
            onClick={() => {
              // Clear all filter values
              setPropSearch([]);
              searchFunction('');
              if (onClearFitler) {
                onClearFitler();
              }
            }}
            className="h-7 md:h-8 px-2 md:px-3"
          >
            <Iconify icon="lucide:filter-x" className="w-3.5 h-3.5 md:w-4 md:h-4 md:mr-1" />
            <span className="hidden md:inline">Clear</span>
          </Button>

          {/* Action Button */}
          {btnAction && (
            <Button
              variant="outline"
              size="sm"
              onClick={btnAction}
              className="text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300 transition-colors duration-200 h-7 md:h-8 px-2 md:px-3 text-xs md:text-sm"
            >
              <Iconify icon="mingcute:add-line" className="w-3.5 h-3.5 md:w-4 md:h-4 md:mr-1" />
              <span className="hidden md:inline">{btnText || ''}</span>
            </Button>
          )}
        </div>
      </div>

      {/* Filter Content */}
      <Collapse in={collapse} timeout="auto">
        <div className="pt-1 md:pt-2">
          {FilterComponent && (
            <FilterComponent
              filterColumn={filterColumn}
              searchFunction={searchFunction}
              searchValue={searchValue}
              propSearch={propSearch}
              setPropSearch={setPropSearch}
            />
          )}
        </div>
      </Collapse>
    </div>
  );
};
